import {memo, type ReactNode} from "react";
import {Badge, Box, Flex, Icon, Spinner, Stack} from "@chakra-ui/react";
import {CheckCircleIcon, LockClosedIcon, ChevronDownIcon} from "@heroicons/react/20/solid";
import type {AuditPlanPageStatus, ChildrenStatusSummary} from "platformed-client/types";
import {ProgressBar as ChakraProgressBar, ProgressRoot} from "../../../../components/ui/progress";
import {Tooltip} from "../../../../components/ui/tooltip";
import {Button} from "../../../../components/ui/button";
import {MenuContent, MenuItem, MenuRoot, MenuTrigger} from "../../../../components/ui/menu";
import T from "platformed-components/i18n/T";
import useTranslate from "platformed-components/i18n/useTranslate";
import {PLAN_PAGE_STATUSES, STATUS_COLORS, STATUS_LABEL_KEYS, STATUS_SUMMARY_FIELDS} from "./statusConstants";
import {ButtonContentSizer} from "../components/ButtonContentSizer";

const STATUS_OPTIONS: AuditPlanPageStatus[] = ["NotStarted", "InProgress", "InReview", "Complete"];

/** One line per status children are actually in: "Not started (1)". */
const StatusBreakdown = ({summary}: {summary: ChildrenStatusSummary}) => {
  const t = useTranslate();

  return (
    <Stack gap={1} fontSize="xs">
      {PLAN_PAGE_STATUSES.map(status => ({status, count: summary[STATUS_SUMMARY_FIELDS[status]]}))
        .filter(({count}) => count > 0)
        .map(({status, count}) => (
          <span key={status}>
            {t(STATUS_LABEL_KEYS[status])} ({count})
          </span>
        ))}
    </Stack>
  );
};

/** Fixed 16px box that centres a status icon, keeping rows vertically aligned. */
const IconFrame = ({children}: {children: ReactNode}) => (
  <Flex w="16px" h="16px" align="center" justify="center" flexShrink={0}>
    {children}
  </Flex>
);

export const StatusBadge = memo(({status}: {status: AuditPlanPageStatus}) => {
  const t = useTranslate();
  return (
    <Badge colorPalette={STATUS_COLORS[status]} size="sm" variant="subtle" flexShrink={0}>
      {t(STATUS_LABEL_KEYS[status])}
    </Badge>
  );
});

export const ProgressBar = memo(({summary}: {summary: ChildrenStatusSummary}) => {
  const {total, complete} = summary;
  if (total === 0) return null;
  const pct = (complete / total) * 100;
  const isFull = complete === total;

  return (
    <Tooltip content={<StatusBreakdown summary={summary} />} openDelay={200} positioning={{placement: "bottom-start"}}>
      <ProgressRoot value={pct} max={100} size="xs" colorPalette={isFull ? "green" : "blue"}>
        <ChakraProgressBar />
      </ProgressRoot>
    </Tooltip>
  );
});

export const StatusIndicator = memo(({status, locked}: {status: AuditPlanPageStatus; locked: boolean}) => {
  const t = useTranslate();
  const label = locked ? t("c.Locked") : t(STATUS_LABEL_KEYS[status]);
  const active = status === "InProgress" || status === "InReview";

  return (
    <Tooltip content={label} openDelay={200}>
      <IconFrame>
        {locked ? (
          <Icon as={LockClosedIcon} color="green.500" boxSize="3" />
        ) : status === "Complete" ? (
          <Icon as={CheckCircleIcon} color="green.500" boxSize="4" />
        ) : (
          <Box w="2" h="2" borderRadius="full" bg={active ? "blue.400" : "gray.400"} />
        )}
      </IconFrame>
    </Tooltip>
  );
});

/** Spinner shown in place of the status pip while a page's automation (understanding,
 * complexity or assessment) is still running. */
export const AutomatingSpinner = memo(() => {
  const t = useTranslate();
  return (
    <Tooltip content={t("c.Automating")} openDelay={200}>
      <IconFrame>
        <Spinner boxSize="3" borderWidth="1.5px" color="purple.500" />
      </IconFrame>
    </Tooltip>
  );
});

export const MiniStatusDonut = memo(({summary, locked}: {summary: ChildrenStatusSummary; locked: boolean}) => {
  const t = useTranslate();
  const {total, complete} = summary;
  if (total === 0) return <StatusIndicator status="NotStarted" locked={locked} />;

  if (locked) {
    const color = complete === total ? "green.500" : complete > 0 ? "blue.500" : "gray.400";
    return (
      <Tooltip
        content={
          <Stack gap={1} fontSize="xs">
            <span>{t("c.Locked")}</span>
            <StatusBreakdown summary={summary} />
          </Stack>
        }
        openDelay={200}
      >
        <IconFrame>
          <Icon as={LockClosedIcon} color={color} boxSize="3" />
        </IconFrame>
      </Tooltip>
    );
  }

  const size = 14;
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const filledLen = (complete / total) * circumference;
  const isFull = complete === total;
  const fillColor = isFull ? "var(--chakra-colors-green-500)" : "var(--chakra-colors-blue-500)";
  const trackColor = isFull ? "var(--chakra-colors-green-100)" : "var(--chakra-colors-blue-300)";

  return (
    <Tooltip content={<StatusBreakdown summary={summary} />} openDelay={200} positioning={{placement: "bottom-start"}}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={trackColor} strokeWidth={strokeWidth} />
        {filledLen > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={fillColor}
            strokeWidth={strokeWidth}
            strokeDasharray={`${filledLen} ${circumference - filledLen}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )}
      </svg>
    </Tooltip>
  );
});

export const StatusButton = memo(
  ({
    status,
    onStatusChange,
    loading,
    disabledReason,
  }: {
    status: AuditPlanPageStatus;
    onStatusChange: (s: AuditPlanPageStatus) => void;
    loading?: boolean;
    /** Shown in a tooltip over the button, which is disabled while one is given. */
    disabledReason?: ReactNode;
  }) => {
    const t = useTranslate();
    const button = (
      <Button
        size="xs"
        variant="outline"
        colorPalette="gray"
        bg="white"
        loading={loading}
        disabled={disabledReason != null}
      >
        <ButtonContentSizer
          active={STATUS_OPTIONS.indexOf(status)}
          variants={STATUS_OPTIONS.map(s => (
            <>
              {t(STATUS_LABEL_KEYS[s])}
              <Icon asChild boxSize="3.5">
                <ChevronDownIcon />
              </Icon>
            </>
          ))}
        />
      </Button>
    );
    if (disabledReason != null) return <Tooltip content={disabledReason}>{button}</Tooltip>;
    return (
      <MenuRoot>
        <MenuTrigger asChild>{button}</MenuTrigger>
        <MenuContent>
          {STATUS_OPTIONS.map(s => (
            <MenuItem key={s} value={s} onClick={() => onStatusChange(s)}>
              <T keyName={STATUS_LABEL_KEYS[s]} />
            </MenuItem>
          ))}
        </MenuContent>
      </MenuRoot>
    );
  },
);
